import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Upload } from "lucide-react";
import { DIETARY_TAGS, TagIcon } from "@/lib/dietaryTags";

export function MenuItemForm({ open, onOpenChange, editItem, setEditItem, categories, subs, sym, onSave, onPhoto }) {
  if (!editItem) return null;

  const isEdit = !!editItem.id;
  const filteredSubs = subs.filter((s) => s.category_id === editItem.category_id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit item" : "New menu item"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Name</Label>
              <Input value={editItem.name} onChange={(e) => setEditItem({ ...editItem, name: e.target.value })} className="mt-1.5" data-testid="item-name-input" />
            </div>
            <div>
              <Label>Price ({sym})</Label>
              <Input type="number" step="0.01" value={editItem.price} onChange={(e) => setEditItem({ ...editItem, price: e.target.value })} className="mt-1.5" data-testid="item-price-input" />
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <Textarea rows={2} value={editItem.description || ""} onChange={(e) => setEditItem({ ...editItem, description: e.target.value })} className="mt-1.5" data-testid="item-desc-input" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Category</Label>
              <Select value={editItem.category_id} onValueChange={(v) => setEditItem({ ...editItem, category_id: v, subcategory_id: null })}>
                <SelectTrigger className="mt-1.5" data-testid="item-category-select"><SelectValue placeholder="Pick" /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Sub-category</Label>
              <Select value={editItem.subcategory_id || "none"} onValueChange={(v) => setEditItem({ ...editItem, subcategory_id: v === "none" ? null : v })}>
                <SelectTrigger className="mt-1.5" data-testid="item-sub-select"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {filteredSubs.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Dietary tags <span className="text-xs text-muted-foreground font-normal">(select any)</span></Label>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="item-dietary-grid">
              {DIETARY_TAGS.map((t) => {
                const active = (editItem.dietary || []).includes(t.id);
                return (
                  <button
                    type="button"
                    key={t.id}
                    onClick={() => {
                      const curr = editItem.dietary || [];
                      const next = active ? curr.filter((x) => x !== t.id) : [...curr, t.id];
                      setEditItem({ ...editItem, dietary: next });
                    }}
                    data-testid={`diet-tag-${t.id}`}
                    aria-pressed={active}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-semibold transition ${
                      active
                        ? "bg-brand-orange/10 border-brand-orange text-brand-navy"
                        : "bg-white border-border text-foreground/70 hover:border-brand-orange/60"
                    }`}
                  >
                    <TagIcon id={t.id} size={14} />
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Photo</Label>
              <div className="mt-1.5 flex items-center gap-3">
                <div className="w-16 h-16 rounded-md bg-muted overflow-hidden border border-border">
                  {editItem.photo && <img src={editItem.photo} alt="" className="w-full h-full object-cover" />}
                </div>
                <label className="inline-flex">
                  <input type="file" accept="image/*" hidden onChange={onPhoto} data-testid="item-photo-input" />
                  <Button type="button" variant="outline" size="sm" asChild><span><Upload className="w-4 h-4" /> Upload</span></Button>
                </label>
              </div>
            </div>
            <div>
              <Label>Video URL (optional)</Label>
              <Input placeholder="https://..." value={editItem.video_url || ""} onChange={(e) => setEditItem({ ...editItem, video_url: e.target.value })} className="mt-1.5" data-testid="item-video-input" />
            </div>
          </div>
          <div className="flex gap-6">
            <label className="flex items-center gap-2"><Switch checked={editItem.in_stock} onCheckedChange={(v) => setEditItem({ ...editItem, in_stock: v })} data-testid="item-stock-switch" /> Available (uncheck for out-of-stock)</label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-brand-orange hover:bg-[#E65C00] text-white" onClick={onSave} data-testid="item-save-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
